import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../../../api/document';

import { CredentialConfirmationModal } from '../../../CredentialConfirmationModal/index';

import { DocumentStatus } from '../../../../../api/model/document';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import {
  ChangeStatusErrorResponseSchema,
  getDefaultFailureMessage,
} from '../../../DocumentWorkspace/WorkspaceV2/BulkActionsV2/BulkActionButtons/ChangeStatusButtons/ChangeStatusService';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderMakeEffectiveButton } from '../../RenderLogic';

export const MakeEffectiveButton = () => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument, refetchDocumentOverviewData } = useContext(
    DocumentOverviewContext,
  );
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { showToast } = useToastProvider();

  const commonToastProps = {
    id: 'make-effective-toast',
    replace: true,
  };

  if (!shouldRenderMakeEffectiveButton(qualioDocument, currentUser)) {
    return null;
  }

  const handleClose = () => {
    setDisabled(false);
    setIsModalOpen(false);
  };

  const handleOpenModal = () => {
    setDisabled(true);
    setIsModalOpen(true);
  };

  const handleMakeEffective = async (
    email: string,
    password: string,
    comment = '',
  ) => {
    const payload = {
      status: DocumentStatus.Effective,
      email,
      password,
      comment,
    };
    await documentApi
      .changeStatus(qualioDocument.id, payload)
      .then(() => {
        refetchDocumentOverviewData();
        showToast({
          'data-cy': 'make-effective-button-notify-success-toast',
          ...commonToastProps,
          description: t('success.successfullyMadeEffective'),
          status: 'success',
          title: t('notifications.success'),
        });
        handleClose();
      })
      .catch((error: unknown) => {
        throw error;
      });
  };

  const handleMakeEffectiveError = (error: unknown) => {
    const parsedError = ChangeStatusErrorResponseSchema.safeParse(error);
    showToast({
      'data-cy': 'make-effective-button-notify-error-toast',
      ...commonToastProps,
      status: 'error',
      title: t('documentActions.failedToMakeEffective'),
      description: parsedError.success
        ? parsedError.data
        : getDefaultFailureMessage(),
    });
  };

  return (
    <>
      <QButton
        data-cy="document-make-effective-button"
        isDisabled={isDisabled}
        variant="solid"
        onClick={handleOpenModal}
      >
        {t('buttons.makeEffective')}
      </QButton>
      <CredentialConfirmationModal
        isOpen={isModalOpen}
        onCloseModal={handleClose}
        modalHeading={t('modals.makeDocumentEffective')}
        modalText={t('modals.enterDigitalSignatureMakeEffective')}
        buttonActionLabel={t('buttons.makeEffective')}
        handleStatusChange={handleMakeEffective}
        handleError={handleMakeEffectiveError}
      />
    </>
  );
};
